import { Injectable } from '@angular/core';
import Onboard, { WalletState } from '@web3-onboard/core';
import injectedModule from '@web3-onboard/injected-wallets';
import walletConnectModule from '@web3-onboard/walletconnect';
import { BehaviorSubject, Observable } from 'rxjs';
import networks from '../../shared/data/networks.json';
import { NetworkModel } from '../models/network.model';

declare const Web3: any;

@Injectable({
  providedIn: 'root',
})
export class BlocknativeService {
  get wallet(): WalletState | null {
    return this.walletSubject.value;
  }

  get web3(): any {
    if (!this.wallet) {
      return null;
    }
    return new Web3(this.wallet.provider);
  }

  get address(): string | null {
    return this.addressSubject.value;
  }

  get networkId(): number | null {
    return this.networkIdSubject.value;
  }

  constructor() {
    this.onboard.state.select('wallets').subscribe(([wallet]) => {
      if (!wallet) {
        return;
      }

      this.walletSubject.next(wallet);
      this.addressSubject.next(wallet.accounts[0].address);

      if (
        !networks
          .map((network) => network.params[0].chainId)
          .includes(wallet.chains[0].id)
      ) {
        this.changeNetwork(networks[0]);
      } else {
        this.networkIdSubject.next(parseInt(wallet.chains[0].id));
        localStorage.setItem('NETWORK_ID', wallet.chains[0].id);
      }
    });
  }

  private walletSubject = new BehaviorSubject<WalletState | null>(null);
  wallet$: Observable<WalletState | null> = this.walletSubject.asObservable();
  private addressSubject = new BehaviorSubject<string | null>(null);
  address$: Observable<string | null> = this.addressSubject.asObservable();
  private networkIdSubject = new BehaviorSubject<number | null>(null);
  networkId$: Observable<any> = this.networkIdSubject.asObservable();

  private onboard = Onboard({
    wallets: [
      injectedModule(),
      walletConnectModule({
        version: 2,
        projectId: '242613167896f041a4647dab3a6d1e9a',
        qrModalOptions: {
          themeVariables: {
            '--wcm-z-index': '999999',
          },
        },
        dappUrl: window.origin,
      }),
    ],
    chains: networks.map((network) => ({
      id: network.params[0].chainId,
      label: network.params[0].chainName,
      rpcUrl: network.params[0].rpcUrls[0],
      token: network.params[0].nativeCurrency.symbol,
    })),
  });

  async init(): Promise<any> {
    let wallets;
    if (localStorage.getItem('SELECTED_WALLET')) {
      wallets = await this.onboard.connectWallet({
        autoSelect: {
          label: localStorage.getItem('SELECTED_WALLET') || '',
          disableModals: true,
        },
      });
    } else {
      wallets = await this.onboard.connectWallet();
    }

    if (wallets.length) {
      localStorage.setItem('SELECTED_WALLET', wallets[0].label);
    } else {
      this.onboard.disconnectWallet({ label: '' });
      localStorage.removeItem('SELECTED_WALLET');
    }
  }

  logout(): void {
    const [wallet] = this.onboard.state.get().wallets;
    this.onboard.disconnectWallet({ label: wallet.label });
    localStorage.removeItem('SELECTED_WALLET');
    this.walletSubject.next(null);
    this.init();
  }

  changeNetwork(network: NetworkModel): void {
    this.onboard.setChain({ chainId: network.params[0].chainId });
  }
}
